import React, { useEffect, useContext, useState, useRef } from 'react'
import styled from 'styled-components'
import { useNavigate, useLocation } from 'react-router-dom'
import ReactFlow, { Background, Controls, MarkerType, ReactFlowProvider, BaseEdge } from 'reactflow'
import 'reactflow/dist/style.css'
import dagre from 'dagre'
import EnvironmentContext  from '../context/EnvironmentContext'
import Network from '../components/Network'
import Address from '../components/Address'
import arrowDown from '../assets/arrow-down.svg'
import { SupportedChainId } from '../constants/chainInfo'

const FlowChart = () => {
  const environment = useContext(EnvironmentContext)
  const [selectedNetwork, setSelectedNetwork] = useState('')
  const [filters, setFilters] = useState({})
  const [tasks, setTasks] = useState(null)
  const [connectors, setConnectors] = useState(null)
  const [filteredTasks, setFilteredTasks] = useState([]) // Estado para las tareas filtradas
  const [hideBalanceConnectors, setHideBalanceConnectors] = useState(false) // New state for hiding balance connectors
  const location = useLocation()
  const navigate = useNavigate()
  const buttonRef = useRef(null)

  const handleSelectNetwork = (e) => {
    setSelectedNetwork(e.target.value)
    updateURL({ ...filters, chainId: e.target.value })
  }

  const handleImageClick = () => {
    buttonRef.current.dispatchEvent(new Event('click', { bubbles: true }))
  }

  const handleHideBalanceConnectors = (e) => {
    setHideBalanceConnectors(e.target.checked)
  }

  const updateURL = (newFilters) => {
    const searchParams = new URLSearchParams()
    Object.keys(newFilters).forEach(key => {
      if (newFilters[key]) {
        searchParams.set(key, newFilters[key])
      } else {
        searchParams.delete(key)
      }
    })
    const queryString = searchParams.toString()
    const newURL = queryString ? `${location.pathname}?${queryString}` : location.pathname

    navigate(newURL, { replace: true })
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const chainId = searchParams.get('chainId') || selectedNetwork
    setFilters({ chainId })
    setSelectedNetwork(chainId)
    updateURL({ chainId })

    // Filter tasks based on selected network
    const allTasks = environment?.data?.tasks
    if (allTasks && chainId) {
      const filteredTasks = allTasks.filter(task => {
        // Check if the task has permissions for the selected chain
        return task.chainPermissions?.some(permission => 
          permission.chainId === chainId && permission.permissions > 0
        )
      })

      const tasks = filteredTasks.map(item => {
        const balanceConnector = item?.taskConfig?.['Balance Connector']?.find(
          connector => connector.network === parseInt(chainId)
        )
        return {
          name: item.name,
          id: item.id,
          previousBalanceConnector: balanceConnector?.previousBalanceConnector,
          nextBalanceConnector: balanceConnector?.nextBalanceConnector,
        }
      })

      const connectorsSet = new Set()

      tasks?.forEach(item => {
        if (item.previousBalanceConnector) {
          connectorsSet.add(item.previousBalanceConnector)
        }
        if (item.nextBalanceConnector) {
          connectorsSet.add(item.nextBalanceConnector)
        }
      })

      const connectors = Array.from(connectorsSet)?.map(connectorId => ({
        id: connectorId
      }))

      setTasks(tasks)
      setConnectors(connectors)
    } else {
      setTasks(null)
      setConnectors(null)
    }
  }, [selectedNetwork, environment?.data?.tasks])

  const toggleTaskFilter = (taskId) => {
    setFilteredTasks(prevFilteredTasks =>
      prevFilteredTasks.includes(taskId)
        ? prevFilteredTasks.filter(id => id !== taskId)
        : [...prevFilteredTasks, taskId]
    )
  }

  const filteredTasksSet = new Set(filteredTasks)
  const filteredNodes = tasks?.filter(task => !filteredTasksSet.has(task.id))
  const filteredConnectors = connectors?.filter(connector =>
    !filteredTasksSet.has(connector.id)
  )

  return (
    <DiagrammSection>
      {!environment.isLoading && (
        <Flex>
          <div className="custom-select-container">
            <select
              value={selectedNetwork}
              onChange={handleSelectNetwork}
              className="custom-select"
              ref={buttonRef}
            >
              <option value="">Select Network</option>
              {Array.from(
                new Set(
                  environment?.data?.tasks?.flatMap(task => 
                    task.chainPermissions?.map(permission => permission.chainId) || []
                  )
                )
              ).map((network) => (
                <option key={network} value={network}>
                  <Network network={network} noLogo={true} />
                </option>
              ))}
            </select>
            <div id="arrow-container" className="arrow-container" onClick={handleImageClick}>
              <img src={arrowDown} alt="Arrow" />
            </div>
          </div>
          
          <div className="checkbox-container">
            <input 
              type="checkbox" 
              id="hide-connectors" 
              checked={hideBalanceConnectors}
              onChange={handleHideBalanceConnectors}
            />
            <label htmlFor="hide-connectors">View without balance connectors</label>
          </div>

          {selectedNetwork && (
            <InfoMessage>
              Showing tasks and connectors for <strong>{selectedNetwork}</strong> network only
            </InfoMessage>
          )}
        </Flex>
      )}

      {tasks && (
        <div className="task-filters">
          {tasks.map(task => (
            <TaskFilterButton
              key={task.id}
              className={filteredTasks.includes(task.id) ? 'disabled' : 'active'}
              onClick={() => toggleTaskFilter(task.id)}
            >
              {task.name}
            </TaskFilterButton>
          ))}
        </div>
      )}

      {filteredNodes && connectors && selectedNetwork ? (
        <Diagram tasks={filteredNodes} connectors={filteredConnectors} chainId={selectedNetwork} hideBalanceConnectors={hideBalanceConnectors}/>
      ) : selectedNetwork ? (
        <div style={{ textAlign: 'center', padding: '50px', color: '#A7ACC1' }}>
          No tasks found for the selected network
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px', color: '#A7ACC1' }}>
          Please select a network to view the diagram
        </div>
      )}
    </DiagrammSection>
  )
}

const TaskFilterButton = styled.button`
  margin: 0 10px;
  padding: 10px;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  background: transparent;
  font-weight: bold;

  &.active {
    color: white;

  }

  &.disabled {
    color: grey;
  }

  &:hover {
    opacity: 0.8;
  }
`


const DiagrammSection = styled.section`
  height: auto;
  padding: 100px 50px 20px 50px;
  color: ${props => props.theme.textWhite};
  width: 100%;
  margin: auto;
  .task-filters {
    padding: 0 50px;
  }
`

const Flex = styled.div`
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 30px;
 .custom-select-container {
   position: relative;
   display: inline-block;
 }

 .custom-select {
   padding: 6px 13px 6px 16px;
   min-width: 150px;
   border: 0px;
   background: rgba(168, 154, 255, 0.10);
   color: white;
   border-radius: 10px;
   font-family: 'DMSans';
   font-size: 17px;
   font-style: normal;
   font-weight: 400;
   line-height: 32px;
   outline: none !important;
   margin-bottom: 30px;
   -webkit-appearance: none;
   -moz-appearance: none;
   appearance: none;
 }

 .arrow-container {
   position: absolute;
   transform: translateY(-50%);
   top: 35%;
   right: 13px;
 }

 .arrow-container img {
   height: 15px;
   width: 15px;
   object-fit: contain;
   height: 100%;
 }
`

const Diagram = ({ tasks, connectors, chainId, hideBalanceConnectors }) => {
  const startNodeId = 'start-node'
  const endNodeId = 'end-node'
  const zeroAddress = '0x0000000000000000000000000000000000000000000000000000000000000000'

  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])

  const initialNodes = [
    {
      id: startNodeId,
      data: { label: <Address address={'0x0000000000000000000000000000000000000000000000000000000000000000'} short /> },
      position: { x: 0, y: 0 },
      draggable: false,
      style: { backgroundColor: '#3c3b60' }
    },
    {
      id: endNodeId,
      data: { label: <Address address={'0x0000000000000000000000000000000000000000000000000000000000000000'} short /> },
      position: { x: 0, y: 0 },
      draggable: false,
      style: { backgroundColor: '#3c3b60' }
    },
    ...(tasks ?? []).map(task => ({
      id: task.id,
      data: { label: task.name, id: task.id },
      position: { x: 0, y: 0 },
      draggable: false,
    })),
    // Only include connector nodes if we're not hiding them
    ...(!hideBalanceConnectors ? (connectors ?? []).filter(connector => connector.id !== zeroAddress).map(connector => ({
      id: connector.id,
      data: { label: <Address address={connector.id} short />, id: connector.id },
      position: { x: 0, y: 0 },
      draggable: false,
      style: { backgroundColor: '#3c3b60' }
    })) : []),
  ]

  // Create a mapping of connectors to tasks for direct connections
  const createTaskConnections = () => {
    if (!hideBalanceConnectors) return []
    
    const connectorToTasks = {}
    
    // Map which tasks connect to which connectors
    tasks?.forEach(task => {
      if (task.previousBalanceConnector && task.previousBalanceConnector !== zeroAddress) {
        if (!connectorToTasks[task.previousBalanceConnector]) {
          connectorToTasks[task.previousBalanceConnector] = { incoming: [], outgoing: [] }
        }
        connectorToTasks[task.previousBalanceConnector].incoming.push(task.id)
      }
      
      if (task.nextBalanceConnector && task.nextBalanceConnector !== zeroAddress) {
        if (!connectorToTasks[task.nextBalanceConnector]) {
          connectorToTasks[task.nextBalanceConnector] = { incoming: [], outgoing: [] }
        }
        connectorToTasks[task.nextBalanceConnector].outgoing.push(task.id)
      }
    })
    
    // Create direct connections between tasks that share connectors
    const directConnections = []
    Object.values(connectorToTasks).forEach(connector => {
      connector.outgoing.forEach(outgoingTask => {
        connector.incoming.forEach(incomingTask => {
          directConnections.push({
            id: `edge-direct-${outgoingTask}-${incomingTask}`,
            source: outgoingTask,
            target: incomingTask,
            type: 'smoothstep',
            animated: false,
            markerEnd: {
              type: MarkerType.ArrowClosed,
            },
            style: { pointerEvents: 'none', stroke: '#10b981' }, // Green color for direct connections
          })
        })
      })
    })
    
    return directConnections
  }

  const initialEdges = [
    ...(hideBalanceConnectors ? createTaskConnections() : [
      // Original logic for connections through connectors
      ...(tasks ?? []).filter(task => task.previousBalanceConnector).map(task => {
        const source = task.previousBalanceConnector === zeroAddress ? startNodeId : task.previousBalanceConnector
        return {
          id: `edge-${source}-${task.id}`,
          source: source,
          target: task.id,
          type: 'smoothstep',
          animated: false,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
          style: { pointerEvents: 'none' },
        }
      }),
      ...(tasks ?? []).filter(task => task.nextBalanceConnector).map(task => {
        const target = task.nextBalanceConnector === zeroAddress ? endNodeId : task.nextBalanceConnector
        return {
          id: `edge-${task.id}-${target}`,
          source: task.id,
          target: target,
          type: 'smoothstep',
          animated: false,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
          style: { pointerEvents: 'none' },
        }
      })
    ]),
    // Add connections from start node to tasks that have zero address as previous connector
    ...(tasks ?? []).filter(task => task.previousBalanceConnector === zeroAddress).map(task => ({
      id: `edge-start-${task.id}`,
      source: startNodeId,
      target: task.id,
      type: 'smoothstep',
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
      },
      style: { pointerEvents: 'none' },
    })),
    // Add connections from tasks to end node that have zero address as next connector
    ...(tasks ?? []).filter(task => task.nextBalanceConnector === zeroAddress).map(task => ({
      id: `edge-${task.id}-end`,
      source: task.id,
      target: endNodeId,
      type: 'smoothstep',
      animated: false,
      markerEnd: {
        type: MarkerType.ArrowClosed,
      },
      style: { pointerEvents: 'none' },
    })),
  ]

  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))

  const nodeWidth = 172
  const nodeHeight = 36

  const getLayoutedElements = (nodes, edges) => {
    dagreGraph.setGraph({ rankdir: 'TB' })

    nodes.forEach((node) => {
      dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight })
    })

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target)
    })

    dagre.layout(dagreGraph)

    nodes.forEach((node) => {
      const nodeWithPosition = dagreGraph.node(node.id)
      node.position = {
        x: nodeWithPosition.x - nodeWidth / 2,
        y: nodeWithPosition.y - nodeHeight / 2,
      }
    })

    return { nodes, edges }
  }

  useEffect(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(initialNodes, initialEdges)
    setNodes([...layoutedNodes])
    setEdges([...layoutedEdges])
  }, [tasks, connectors, chainId, hideBalanceConnectors])


  return (
    <DiagramSection>
      <div style={{ height: '100vh', width: '100%' }}>
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            fitView
            elementsSelectable={false}
            nodesDraggable={false}
            edgesUpdatable={false}
          >
            <Background />
            <Controls />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </DiagramSection>
  )
}

const DiagramSection = styled.section`
  height: auto;
  padding: 20px 0;
  color: ${(props) => props.theme.textWhite};
  width: 100%;
  margin: auto;
`

const InfoMessage = styled.div`
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  font-size: 14px;
  color: ${props => props.theme.textWhite};
`

export default FlowChart
